import { enterpriseLastMileDelivery } from '../utils/content-settings';
import {
  inferVisiblityOptionFromPriceSettings,
  updatePriceSettingsFromVisibilityOption,
} from '../utils/pricing';
import {
  adjustBroadcastDatesForCrossingMidnight,
  unreplaceDateTokens,
  getDatesForClonedBroadcast,
} from '../utils/date';
import { checkContentSettingsForChangesOnSave } from '../utils/password';
import { IsRtmpURLValid } from '../utils/rtmp';
import { FindTimeZone } from '../utils/timezone';
import {
  restoreScoreboardUIConfiguration,
  mapScoreboardUIConfigurationToAPI,
} from '../utils/scoreboard';
import { VideoAudioConfig } from '../utils/video-and-audio-config';
import {
  EnhancedBoxCastFlowControlTooltipHTML,
  MaximumBoxCastFlowControlTooltipHTML,
} from '../utils/boxcast_flow_control';

let module = angular.module('edit-or-create-event-service', []);

// Emulate factory pattern
export class EditOrCreateEventService {
  constructor(
    $q,
    BoxCaster,
    Broadcast,
    Recording,
    Account,
    Channel,
    BroadcastTags,
    $rootScope,
    YouTubeUser,
    StaticRTMP,
    Facebook,
    TwitterUser,
    RTMPPushChannel,
    toaster,
    $window,
    $uibModal,
    $sce
  ) {
    'ngInject';

    this.$q = $q;
    this.Broadcast = Broadcast;
    this.Recording = Recording;
    this.BoxCaster = BoxCaster;
    this.Account = Account;
    this.Channel = Channel;
    this.BroadcastTags = BroadcastTags;
    this.$rootScope = $rootScope;
    this.YouTubeUser = YouTubeUser;
    this.StaticRTMP = StaticRTMP;
    this.Facebook = Facebook;
    this.TwitterUser = TwitterUser;
    this.RTMPPushChannel = RTMPPushChannel;
    this.toaster = toaster;
    this.$window = $window;
    this.$uibModal = $uibModal;
    this.$sce = $sce;
  }

  getInstance() {
    return new _EditOrCreateEventServiceImpl(
      this.$q,
      this.BoxCaster,
      this.Broadcast,
      this.Recording,
      this.Account,
      this.Channel,
      this.BroadcastTags,
      this.$rootScope,
      this.YouTubeUser,
      this.StaticRTMP,
      this.Facebook,
      this.TwitterUser,
      this.RTMPPushChannel,
      this.toaster,
      this.$window,
      this.$uibModal,
      this.$sce
    );
  }

  activateBroadcast(broadcast) {
    // XXX: activating a broadcast means resetting the start/end date.
    // If we're in pre-roll, keep the same end date, but if you're trying to activate
    // something much further out, keep the exact same duration.
    var stops_at = broadcast.stops_at;
    if (broadcast.timeframe == 'future') {
      var duration = moment(broadcast.stops_at) - moment(broadcast.starts_at);
      stops_at = moment().add(duration).toDate().toISOString();
    }
    return this.Broadcast.update(
      { id: broadcast.id },
      {
        stream_source: 'switcher',
        starts_at: 'now',
        stops_at: stops_at,
      }
    ).$promise;
  }

  stopBroadcast(broadcast) {
    return this.Broadcast.update({ id: broadcast.id }, { stops_at: 'now' }).$promise;
  }
}

export class _EditOrCreateEventServiceImpl {
  constructor(
    $q,
    BoxCaster,
    Broadcast,
    Recording,
    Account,
    Channel,
    BroadcastTags,
    $rootScope,
    YouTubeUser,
    StaticRTMP,
    Facebook,
    TwitterUser,
    RTMPPushChannel,
    toaster,
    $window,
    $uibModal,
    $sce
  ) {
    'ngInject';

    this.$q = $q;
    this.Broadcast = Broadcast;
    this.Recording = Recording;
    this.ApiConfig = $rootScope.ApiConfig;
    this.account = $rootScope.account;
    this.user = $rootScope.user;
    this.FeatureFlags = this.account.feature_flags;
    this.BoxCaster = BoxCaster;
    this.Account = Account;
    this.Channel = Channel;
    this.BroadcastTags = BroadcastTags;
    this.YouTubeUser = YouTubeUser;
    this.StaticRTMP = StaticRTMP;
    this.fb = Facebook;
    this.TwitterUser = TwitterUser;
    this.RTMPPushChannel = RTMPPushChannel;
    this.toaster = toaster;
    this.$window = $window;
    this.$uibModal = $uibModal;

    this.limits = $rootScope.ApiConfig.modelFieldLimits('broadcast', 'maximum');
    this.genericRTMPPushLimits = $rootScope.ApiConfig.modelFieldLimits(
      'generic_rtmp_push',
      'maximum'
    );

    this.canDelete = $rootScope.user.can('delete', 'broadcast/*');

    this.enhancedBoxCastFlowControlTooltip = $sce.trustAsHtml(
      EnhancedBoxCastFlowControlTooltipHTML
    );
    this.maximumBoxCastFlowControlTooltip = $sce.trustAsHtml(MaximumBoxCastFlowControlTooltipHTML);

    this.selectedBoxCaster = null;
    this.scrambleChannelId = true; //... but only if the scramble option is shown
    this.bulkImportMode = false;

    this.initConfig();
    this.initRelatedData();
  }

  initScope($scope) {
    const { ApiConfig, BoxCaster } = this;

    this.$scope = $scope;

    $scope.$watchCollection(
      () => [this.startDate, this.startTime, this.endTime],
      () => this.compensateEndDate()
    );

    $scope.$watch(
      () => this.boxCasterIdSafe,
      (id) => {
        this.selectedBoxCaster = _.findWhere(this.boxcasters, { id: id });
        this.initAdvancedSettingsBySource();
        if (id && !id.startsWith('stream_source:')) {
          this.boxcaster = BoxCaster.get({ id: id }, () => this.initAdvancedSettingsBySource());
        } else {
          this.boxcaster = null;
        }
      }
    );

    $scope.$watchCollection(
      () => [this.startTime, this.sourceBroadcast],
      () => {
        const durationSeconds = (this.sourceBroadcast || {}).recording_duration_seconds;
        if (!durationSeconds) {
          this.simulatedLiveEndTime = null;
          return;
        }
        this.simulatedLiveEndTime = moment(this.startTime).add(durationSeconds, 'seconds').toDate();
      }
    );

    $scope.$watch(
      () => this.boxCasterIdSafe,
      (newValue, oldValue) => {
        if (oldValue == 'stream_source:recording' && newValue != 'stream_source:recording') {
          // If switching from simulated live, initialize advanced settings to sane defaults
          this.broadcast.do_not_record = this.doNotRecordDefault;
          this.broadcast.requests_captioning = this.requestsCaptioningDefault;
        } else if (newValue == 'stream_source:recording' && oldValue != 'stream_source:recording') {
          // If switching to simulated live, initialize advanced settings.
          if (oldValue || !this.broadcast.id) {
            // If switching TO simulated live either from some other non-empty value (like an
            // actual BoxCaster) OR this is a new broadcast, set live-only from accout default.
            // The $watch can and will fire when the broadcast loads as well, so we have to be extra
            // careful we don't touch this unless it was something before (i.e. this is not the
            // initial load) or it's a new broadcast (there's no ID yet), where it makes sense to
            // be toggling values at startup.
            this.broadcast.do_not_record = !this.account.default_record_simulated_live;
          }
          this.broadcast.requests_captioning = false;
        } else if (oldValue == 'stream_source:app' && newValue != 'stream_source:app') {
          // Switching from Broadcaster app to something else
          if (this.broadcast && this.broadcast.target_buffer_latency == 'standard') {
            // If the user had selected standard latency, put it back to 'default'
            this.broadcast.target_buffer_latency = 'default';
          }
        }
      }
    );
  }

  initConfig() {
    const { account, ApiConfig, FeatureFlags } = this;

    // Get account feature flags
    this.showAutomatedScoreboard = FeatureFlags.automated_scoreboard;
    this.showSportzcast = FeatureFlags.sportzcast;
    this.showManualScoreboard = FeatureFlags.manual_scoreboard;
    this.showAnyScoreboards =
      this.showAutomatedScoreboard || this.showSportzcast || this.showManualScoreboard;
    this.showFlex = FeatureFlags.flex;
    this.showPublic = !FeatureFlags.no_public;
    this.showTicketed = !FeatureFlags.no_ticketed;
    this.showFeatured = account.extra_features.featured_broadcasts;
    this.showSportzCast = FeatureFlags.sportzcast;
    this.allowBroadcasterApp =
      account.usage_flags.broadcaster_app && !FeatureFlags.broadcaster_app_source_disabled;
    this.allowRtmp = FeatureFlags.rtmp;
    this.allowSwitcher = account.usage_flags.switcher;
    this.allowFacebookLive = FeatureFlags.facebook_live;
    this.allowYoutubeLive = FeatureFlags.youtube_live;
    this.allowGenericRtmpPush = FeatureFlags.generic_rtmp_push;
    this.allowTwitter = FeatureFlags.twitter;
    this.allowSimulatedLive = FeatureFlags.simulated_live;
    this.allowDefaultOverlay = true;
    this.resolutionLimit = account.resolution_limit;
    this.allowHighFrameRate = FeatureFlags.high_frame_rate;
    this.maxBroadcastDurationHours = account.max_broadcast_duration_hrs;
    this.simulcastOnly = FeatureFlags.no_viewers;
    this.simulcastLimit = FeatureFlags.single_destination ? 1 : 999;
    this.allowProducer = FeatureFlags.producer;

    // Grab shared config values
    this.sports = ApiConfig.stream.sports;
    this.scoreboards = ApiConfig.stream.scoreboards;
    this.dscpValues = ApiConfig.stream.dscp_values;
    this.minTicketPrice = ApiConfig.ticket.minimum_price;
    this.initAdvancedSettingsBySource();
  }

  initAdvancedSettingsBySource() {
    const { ApiConfig } = this;

    this.videoResolutions = _.filter(
      ApiConfig.stream.video_resolutions,
      (b) => b.value == 'default' || b.value <= this.resolutionLimit
    );
    this.videoBitrates = VideoAudioConfig.stream.video_bitrates;
    this.audioBitrates = VideoAudioConfig.stream.audio_bitrates;
    this.audioSources = ApiConfig.stream.audio_sources;
    this.highFrameRates = ApiConfig.stream.high_frame_rates;
    this.bufferLatencies = ApiConfig.stream.buffer_latencies;
    // Restrict BoxCaster source capabilities based on capabilities (inferred by serial number prefix)
    if (this.isBoxCaster && this.boxCasterIdSafe) {
      const filter = (b) => _.contains(b.serial_prefixes, this.boxcasterPrefix);
      this.videoResolutions = _.filter(this.videoResolutions, filter);
      this.videoBitrates = _.filter(this.videoBitrates, filter);
      this.audioBitrates = _.filter(this.audioBitrates, filter);
      this.audioSources = _.filter(this.audioSources, filter);
      this.highFrameRates = _.filter(this.highFrameRates, filter);
      this.bufferLatencies = _.filter(this.bufferLatencies, filter);

      if (this.isBoxCasterPro) {
        // BoxCaster Pro doesn't currently support audio-only.
        this.videoBitrates = _.filter(
          this.videoBitrates,
          (b) => b.value != AUDIO_ONLY_BITRATE_HINT
        );
      }
    }
    // Prevent scoreboards and overlays for simulated live as well as producer broadcasts
    if (this.isSimulatedLive || this.isProducerBroadcast) {
      this.showAnyScoreboards = false;
      this.allowDefaultOverlay = false;
    } else {
      this.showAnyScoreboards =
        this.showAutomatedScoreboard || this.showSportzcast || this.showManualScoreboard;
      this.allowDefaultOverlay = true;
    }
    // Default RTMP sources to the account resolution and frame rate limit
    if (this.broadcast && this.broadcast.timeframe != 'past') {
      if (this.isRTMPBasedSource) {
        if (this.broadcast.target_video_resolution == 'default') {
          this.broadcast.target_video_resolution = this.resolutionLimit;
        }
        if (this.broadcast.target_high_frame_rate == 'default') {
          this.broadcast.target_high_frame_rate = this.allowHighFrameRate;
        }
      }
    }
    // Dynamic advanced settings labels
    const overridden = (field) => {
      return this.boxcaster && this.boxcaster[field] && this.boxcaster[field] != 'default';
    };
    const sourceName = this.isBroadcasterApp ? 'Broadcaster App' : 'BoxCaster';
    const updateLabel = (field, settings, defaultVal = 'Auto-detect') => {
      if (!settings || !settings.length) {
        return;
      } else if (this.isRTMPBasedSource) {
        settings[0].label = `Default`;
      } else if (overridden(field)) {
        var label = (_.first(_.filter(settings, (s) => s.value == this.boxcaster[field])) || {})
          .label;
        settings[0].label = `${sourceName} Default: ${label}`;
      } else {
        settings[0].label = defaultVal;
      }
    };
    updateLabel('target_video_bitrate', this.videoBitrates);
    updateLabel('target_audio_bitrate', this.audioBitrates);
    updateLabel('audio_source', this.audioSources, 'All Sources');
    updateLabel('target_video_resolution', this.videoResolutions);
    updateLabel('target_high_frame_rate', this.highFrameRates);
    updateLabel('dscp_value', this.dscpValues, 'No DSCP');

    if (this.isBroadcasterApp) {
      // For Broadcaster, don't allow the "default" buffer latency (as there's no BoxCaster to "default" to)
      this.bufferLatencies = _.reject(this.bufferLatencies, (v) => v.value == 'default');
      if (this.broadcast && this.broadcast.target_buffer_latency == 'default') {
        this.broadcast.target_buffer_latency = 'standard';
      }
    } else {
      updateLabel('target_buffer_latency', this.bufferLatencies, 'Standard');
    }
  }

  initRelatedData() {
    const {
      BoxCaster,
      Account,
      Channel,
      BroadcastTags,
      FeatureFlags,
      YouTubeUser,
      StaticRTMP,
      TwitterUser,
      RTMPPushChannel,
      $q,
    } = this;

    this.boxcasters = BoxCaster.query({ l: 1000, s: 'name' }, () => {
      this.selectedBoxCaster = _.findWhere(this.boxcasters, { id: this.boxCasterIdSafe });
    });
    if (FeatureFlags.no_viewers) {
      this.channels = [];
    } else {
      this.channels = Channel.query({ l: 1000 });
    }
    this.tags = BroadcastTags.query();

    this.accountScoreboard = Account.getScoreboard();

    if (this.showSportzcast) {
      this.sportzcast = Account.getSportzcast();
    } else {
      this.sportzcast = {};
    }

    let destinationPromsises = [];

    this.youtubeUsers = [];
    if (this.allowYoutubeLive) {
      destinationPromsises.push(
        YouTubeUser.query().$promise.then((users) => {
          this.youtubeUsers = users.filter(
            (user) => user.scope && _.contains(user.scope, YOUTUBE_SCOPE)
          );
        })
      );
    }

    this.staticRTMPs = [];
    if (this.allowRtmp) {
      this.staticRTMPs = StaticRTMP.query({ l: 1000, include_diagnostics: true });
      destinationPromsises.push(this.staticRTMPs.$promise);
    }

    this.twitterUsers = [];
    if (this.allowTwitter) {
      this.twitterUsers = TwitterUser.query();
      destinationPromsises.push(this.twitterUsers.$promise);
    }

    this.rtmpPushChannels = [];
    if (this.allowGenericRtmpPush) {
      this.rtmpPushChannels = RTMPPushChannel.query();
      destinationPromsises.push(this.rtmpPushChannels.$promise);
    }

    this.destinationPromise = $q.all(destinationPromsises);

    this.sourcesPromise = $q.all([this.boxcasters.$promise, this.staticRTMPs.$promise]).then(() => {
      this.sources = [];
      this.sources = this.sources.concat(
        this.boxcasters.map((b) =>
          angular.extend({ type: `boxcaster${b.id.startsWith('p') ? '_pro' : ''}` }, b)
        )
      );
      if (this.allowSwitcher) {
        this.sources = this.sources.concat({
          type: 'switcher',
          id: 'stream_source:switcher',
          name: 'Switcher Studio (iOS)',
        });
      }
      if (this.allowBroadcasterApp) {
        this.sources = this.sources.concat({
          type: 'broadcaster',
          id: 'stream_source:app',
          name: 'Broadcaster App',
        });
      }
      if (this.allowProducer) {
        this.sources = this.sources.concat({
          type: 'producer',
          id: 'stream_source:producer',
          name: 'Producer (Browser-based streaming)',
        });
      }
      if (this.allowRtmp) {
        this.sources = this.sources.concat(
          this.staticRTMPs.map((r) =>
            angular.extend({ type: 'static_rtmp' }, r, { id: `stream_source:rtmp:${r.id}` })
          )
        );
      }
      const sourceIsEditable = (this.broadcast.timeframe || 'future') == 'future';
      if (this.allowSimulatedLive || (!sourceIsEditable && !this.broadcast.source_broadcast_id)) {
        this.sources = this.sources.concat({
          type: 'recording',
          id: 'stream_source:recording',
          name: 'Re-Broadcast (Simulated Live)',
        });
      }
    });
  }

  initBroadcast(id, clone) {
    const { ApiConfig, Broadcast, FeatureFlags, $q } = this;

    if (id) {
      // Edit broadcast mode...
      this.broadcast = Broadcast.get(
        { id: id, include_recurrence: true },
        () => {
          this.minDate = new Date();

          if (clone) {
            this.broadcast.id = null;
            var { starts_at, stops_at } = getDatesForClonedBroadcast(this.broadcast, moment());
            this.broadcast.starts_at = starts_at;
            this.broadcast.stops_at = stops_at;
            delete this.broadcast.id;
            delete this.broadcast.timeframe;
            delete this.broadcast.channel_id;
            delete this.broadcast.recurrence;
          }

          if (this.broadcast.recurrence && this.broadcast.recurrence.id) {
            // Un-replace date tokens for the purpose of editing (only for broadcasts that are part of a recurrence)
            this.broadcast.name = unreplaceDateTokens(
              this.broadcast.name || '',
              this.broadcast.starts_at
            );
            this.broadcast.description = unreplaceDateTokens(
              this.broadcast.description || '',
              this.broadcast.starts_at
            );
            // Make sure time zone attribute is set for any date changes
            this.broadcast.recurrence.time_zone = FindTimeZone();
          }

          this.startDate = moment(this.broadcast.starts_at).toDate();
          this.startTime = moment(this.broadcast.starts_at).toDate();

          // XXX: compensate for old indefinite broadcasts ... we don't suppport that anymore
          if (!this.broadcast.stops_at || this.broadcast.stops_at.startsWith('9999-12-31')) {
            this.endTime = moment(this.startTime)
              .add(this.maxBroadcastDurationHours, 'hours')
              .subtract(1, 'minute');
          } else {
            this.endTime = moment(this.broadcast.stops_at).toDate();
            // XXX: API stops_at is a derived field; it only really stores starts_at and duration in minutes
            // so we need to work around an off-by-one error when there are non-zero seconds in the starts_at
            if (moment(this.endTime).seconds() > 0) {
              this.endTime = moment(this.endTime).add(1, 'minute').toDate();
            }
            // XXX: Broadcaster for iOS uses 24:00:00 hours for duration rather than 23:59:59, which
            // causes some issues with other assumptions throughout the app. Correct for that minute here.
            if (
              moment(this.startTime).add(1, 'days').format('YYYY-MM-DD HH:mm') ==
              moment(this.endTime).format('YYYY-MM-DD HH:mm')
            ) {
              this.endTime = moment(this.endTime).subtract(1, 'minute').toDate();
            }
          }

          if (this.broadcast.stream_source == 'imported') {
            this.datepickerOptions = { maxDate: this.minDate, showWeeks: false };
          } else {
            this.datepickerOptions = { minDate: this.minDate, showWeeks: false };
          }

          this.visibility = inferVisiblityOptionFromPriceSettings(this.broadcast);
          this.origVisibility = this.visibility;
          this.isFeatured = _.contains(this.broadcast.tags, 'featured');

          if (!this.broadcast.ticket_price) {
            this.broadcast.ticket_price =
              this.account.ticket_price || ApiConfig.ticket.default_price;
          }

          if (this.broadcast.stream_source && this.broadcast.stream_source != 'boxcaster') {
            if (this.broadcast.stream_source == 'rtmp' && this.broadcast.static_rtmp_id) {
              this.broadcast.boxcaster_id = `stream_source:rtmp:${this.broadcast.static_rtmp_id}`;
            } else {
              // If this is static RTMP, add that to the sources list.
              if (this.broadcast.stream_source === 'rtmp') {
                // XXX: Ideally, this would be in the middle of the list next to the other RTMP stream sources, but
                // that logic is really complicated. Since this is meant to be "edit only" (and no one should be
                // configuring new broadcasts with single-use RTMP), we're just going to stick it at the end.
                this.sourcesPromise.then(() => {
                  this.sources = this.sources.concat({
                    type: 'rtmp',
                    id: 'stream_source:rtmp',
                    name: 'Single-Use RTMP',
                  });
                });
              }
              this.broadcast.boxcaster_id = `stream_source:${this.broadcast.stream_source}`;
            }
          }

          // If the previously-selected video bitrate option is "Audio Only", then the user
          // cannot change the bitrate, so we'll use this to disable the select control.
          this.disableVideoBitrate =
            this.broadcast.timeframe == 'past' ||
            (this.broadcast.timeframe == 'current' && this.isAudioOnly);
          this.disableAudioOnlyOption = this.broadcast.timeframe == 'current' && !this.isAudioOnly;

          // Once user selects an actual video bitrate, they can't go back to "default",
          // which really means "auto-detect" (too late)
          this.disableDefaultVideoBitrateOption =
            this.broadcast.timeframe == 'current' &&
            this.broadcast.target_video_bitrate != 'default';

          // integer to string values for max_resolution
          this.broadcast.simulcast_configurations
            .filter((s) => s.destination === 'generic')
            .forEach((s) => (s.max_resolution = s.max_resolution.toString()));

          // Glue `max_resolution` onto each rtmp_push_channel in case the user edits them
          this.broadcast.simulcast_configurations
            .filter((s) => s.destination === 'rtmp_push_channel')
            .forEach((s) => {
              if (!s.max_resolution) {
                s.max_resolution = '0';
              }
            });

          // Separate out feed type and ID for facebook
          this.broadcast.simulcast_configurations
            .filter((s) => s.destination === 'facebook')
            .forEach((s) => {
              if (s.fb_feed_id === 'user:me') {
                s.feed_type = 'user:me';
              } else {
                s.feed_type = s.fb_feed_id.split(':', 1)[0];
                s.feed_id = s.fb_feed_id.replace(/^[^:]*:/, '');
              }
            });

          this.otherEvents = [];
          this.conflictingEvents = [];
          this.worstCaseSimultaneousStreams = 0;
          this.getConflictingEvents(true);

          this.broadcast.content_settings = this.broadcast.content_settings || {};

          restoreScoreboardUIConfiguration(this.broadcast.scoreboard_configuration);

          if (this.broadcast.source_broadcast_id) {
            this.fromBroadcast(this.broadcast.source_broadcast_id, clone);
          }
        },
        (error) => {}
      );

      this.broadcast.$promise.then(() => this.updateScoreboardMakes());
    } else {
      // Create new broadcast mode (no ID specified)...
      this.broadcast = {
        name: null,
        description: null,
        boxcaster_id: null,
        stream_source: 'boxcaster',
        do_not_record: this.doNotRecordDefault,
        sport: null,
        target_high_frame_rate: 'default',
        target_video_bitrate: 'default',
        target_audio_bitrate: 'default',
        transcoder_profile: 'default',
        dscp_value: 'default',
        audio_source: 'default',
        target_video_resolution: 'default',
        target_codec: 'default',
        ticket_price: null,
        free_variant: null,
        in_channel_ids: [],
        simulcast_configurations: [],
        content_settings: {},
        ticket_price: this.account.ticket_price || ApiConfig.ticket.default_price,
        scoreboard_configuration: { overlay_location: 'bottom' },
        metadata: {},
        requests_captioning: this.requestsCaptioningDefault,
        target_buffer_latency: 'default',
      };

      this.isFeatured = false;

      this.accountScoreboard.$promise.then(() => {
        this.broadcast.scoreboard_configuration.home_team_name =
          this.accountScoreboard.home_team_name;
        this.broadcast.scoreboard_configuration.home_team_logo =
          this.accountScoreboard.home_team_logo;
      });

      let now = moment().set({ second: 0, millisecond: 0 });
      this.startDate = now.clone().toDate();
      this.startTime = now.clone().toDate();
      this.originalStartTime = new Date(this.startTime.getTime());
      this.minDate = now.clone().toDate();
      this.endTime = now.clone().add(30, 'minutes').toDate();
      this.originalEndTime = new Date(this.endTime.getTime());
      this.visibility = this.showPublic ? 'public' : 'private';
      this.datepickerOptions = { minDate: this.minDate, showWeeks: false };

      this.otherEvents = [];
      this.conflictingEvents = [];
      this.worstCaseSimultaneousStreams = 0;
      this.getConflictingEvents(true);
    }
  }

  fromBroadcast(broadcastId, clone = false) {
    const { account, broadcast, Broadcast } = this;
    broadcast.stream_source = 'recording';
    Broadcast.get(
      { id: broadcastId },
      (b) => this.updateSourceBroadcast(b),
      (err) => {
        if (err && (err.status == 404 || err.status == 410)) {
          // 404/410 means this source isn't available anymore
          if (clone) {
            // Cloning, so just clear out the source info and let them pick something else
            this.sourceBroadcast = null;
            broadcast.source_broadcast_id = null;
            this.datepickerOptions.minDate = this.minDate;
          } else {
            // Not cloning -- let them know the source broadcast is not available anymore.
            this.sourceBroadcastUnavailable = true;
            this.sourceBroadcastProblem = 'is unavailable';
          }
        } else {
          console.error('Failed to get source broadcast', err);
        }
      }
    );
  }

  updateSourceBroadcast(b) {
    const { account, broadcast } = this;
    this.sourceBroadcast = b;
    broadcast.source_broadcast_id = b.id;
    this.datepickerOptions.minDate = new Date(
      Math.max(this.minDate.getTime(), moment(this.sourceBroadcast.stops_at).toDate().getTime())
    );
    if (account.storage_policy == 'auto-delete') {
      this.datepickerOptions.maxDate = moment(this.sourceBroadcast.storage_expires_at).toDate();
    }
    if (b.timeframe == 'past' && b.recording_duration_seconds <= 0) {
      this.sourceBroadcastProblem = 'does not exist';
    } else if (this.recordingStorageExpires) {
      this.sourceBroadcastProblem = "will be purged before the broadcast's start time";
    }
  }

  get showChannelSelector() {
    // If account doesn't have channels defined, never show it.
    // If it's a private event, only show selector if they've got channels selected
    if (!this.channels || this.channels.length == 0) {
      return false;
    }
    let selectedChannels = this.broadcast.in_channel_ids || [];
    let hasChannelsSelected = selectedChannels.length > 0;
    return this.visibility != 'private' || hasChannelsSelected;
  }

  get showScoreboardModel() {
    return this.scoreboardModels && this.scoreboardModels.length > 1;
  }

  get boxcasterPrefix() {
    return this.boxCasterIdSafe[0];
  }

  get boxCasterIdSafe() {
    return (this.broadcast || {}).boxcaster_id || '';
  }

  get hideOptionRecord() {
    return this.FeatureFlags.no_recordings;
  }

  get isAudioOnly() {
    return !!((this.broadcast || {}).target_video_bitrate <= AUDIO_ONLY_BITRATE_HINT);
  }

  get isBoxCaster() {
    return !this.boxCasterIdSafe.startsWith('stream_source:');
  }

  get isBoxCasterHDSD2() {
    return (this.boxcasterPrefix || '').startsWith('o');
  }

  get isBoxCasterPro() {
    return (this.boxcasterPrefix || '').startsWith('p');
  }

  get isBroadcasterApp() {
    return this.boxCasterIdSafe.startsWith('stream_source:app');
  }

  get isRTMPSelected() {
    return this.boxCasterIdSafe.startsWith('stream_source:rtmp');
  }

  get isSimulatedLive() {
    return this.boxCasterIdSafe == 'stream_source:recording';
  }

  get isSwitcher() {
    return this.boxCasterIdSafe.startsWith('stream_source:switcher');
  }

  get isProducerBroadcast() {
    return this.boxCasterIdSafe.startsWith('stream_source:producer');
  }

  get isRTMPBasedSource() {
    return this.isRTMPSelected || this.isSwitcher;
  }

  get hideOptionTargetAudioBitRate() {
    return this.isRTMPBasedSource || this.isSimulatedLive || this.isProducerBroadcast;
  }

  get hideHighFrameRate() {
    return (
      !this.allowHighFrameRate ||
      this.isSwitcher ||
      this.isSimulatedLive ||
      this.isProducerBroadcast ||
      !(this.isRTMPSelected || this.isBroadcasterApp || this.isBoxCasterPro)
    );
  }

  get hideOptionTargetVideoBitRate() {
    return this.isRTMPBasedSource || this.isSimulatedLive || this.isProducerBroadcast;
  }

  get hideOptionAudioSource() {
    return (
      this.isBroadcasterApp ||
      this.isRTMPBasedSource ||
      this.isSimulatedLive ||
      this.isProducerBroadcast
    );
  }

  get hideOptionVideoResolution() {
    return this.isSimulatedLive || this.isProducerBroadcast;
  }

  get hideOptionDSCPValue() {
    return (
      this.isBroadcasterApp ||
      this.isRTMPBasedSource ||
      this.isSimulatedLive ||
      this.isProducerBroadcast
    );
  }

  get showOptionTargetBufferLatency() {
    // If they don't have the BoxCast Flow Control feature, don't show it
    if (this.account.feature_flags.boxcast_flow_control_disabled) {
      return false;
    }

    // Only show for BoxCaster HDSD2, Pro,  or Broadcaster app
    return this.isBoxCasterHDSD2 || this.isBoxCasterPro || this.isBroadcasterApp;
  }

  get enterpriseLastMileDelivery() {
    return enterpriseLastMileDelivery(this.broadcast);
  }

  get requestsCaptioningDefault() {
    return (
      (this.FeatureFlags.automated_captioning || this.FeatureFlags.live_automated_captioning) &&
      this.account.content_settings.requestsCaptioning
    );
  }

  get doNotRecordDefault() {
    return !!this.FeatureFlags.no_recordings;
  }

  get targetBufferLatencyTooltip() {
    if (this.isBoxCasterPro || this.isBroadcasterApp) {
      return this.maximumBoxCastFlowControlTooltip;
    }
    return this.enhancedBoxCastFlowControlTooltip;
  }

  chooseDefaultSourceForNewBroadcast() {
    // Force a selection if user has multiple BoxCasters registered
    if (this.boxcasters.length > 1) {
      return;
    }

    // Choose one and only BoxCaster
    if (this.boxcasters.length == 1) {
      this.selectedBoxCaster = this.boxcasters[0];
      this.broadcast.boxcaster_id = this.selectedBoxCaster.id;
      return;
    }

    // Force a selection if user has multiple static RTMP channels defined
    if (this.staticRTMPs.length > 1) {
      return;
    }

    // Choose one and only static RTMP channel
    if (this.staticRTMPs.length == 1) {
      this.broadcast.boxcaster_id = `stream_source:rtmp:${this.staticRTMPs[0].id}`;
      return;
    }

    // Otherwise, prefer Broadcaster > Switcher Studio
    if (this.allowBroadcasterApp) {
      this.broadcast.boxcaster_id = 'stream_source:app';
    } else if (this.allowSwitcher) {
      this.broadcast.boxcaster_id = 'stream_source:switcher';
    } else if (this.allowSimulatedLive) {
      this.broadcast.boxcaster_id = 'stream_source:recording';
    }
  }

  initializeEmptyFacebookAccountIDs() {
    (this.broadcast.simulcast_configurations || []).forEach((sc) => {
      if (sc.destination === 'facebook' && !sc.facebook_account_id) {
        sc.facebook_account_id = this.fb.facebook_account_id;
      }
    });
  }

  addSimulcastDestination(destination, params) {
    this.broadcast.simulcast_configurations = this.broadcast.simulcast_configurations || [];
    params = params || {};

    const destConfig = { destination: destination, isHelpRow: params.isHelpRow };
    switch (destination) {
      case 'facebook':
        let id = params.id || this.fb.facebook_account_id;
        if (!id) {
          // only allow 1 "unlinked" account to show up in the UI
          let unlinkedFBConfigs = _.filter(
            this.broadcast.simulcast_configurations,
            (sc) => sc.destination == 'facebook' && !sc.facebook_account_id
          );
          if (unlinkedFBConfigs.length > 0) {
            return;
          }
        }
        destConfig.facebook_account_id = id;
        destConfig.feed_type = 'user:me';
        destConfig.fb_privacy = 'EVERYONE';
        break;

      case 'youtube':
        destConfig.youtube_account_id = params.id || (this.youtubeUsers[0] || {}).id;
        destConfig.privacy = 'unlisted';
        break;

      case 'generic':
        destConfig.stream_url = '';
        destConfig.stream_key = '';
        destConfig.max_resolution = '0';
        break;

      case 'twitter':
        destConfig.twitter_account_id = params.id || (this.twitterUsers[0] || {}).id;
        destConfig.should_not_tweet = false;
        break;

      case 'rtmp_push_channel':
        destConfig.rtmp_push_channel_id = params.id || (this.rtmpPushChannels[0] || {}).id;
        destConfig.max_resolution = '0';
        break;

      default:
        return;
    }
    this.broadcast.simulcast_configurations.push(destConfig);
  }

  removeSimulcastDestination(obj) {
    _.remove(this.broadcast.simulcast_configurations, obj);
  }

  facebookFeedTypeChanged(config) {
    // For pages, events, and groups, only public visibility is permitted.
    switch (config.feed_type) {
      case 'page':
        config.feed_id = (this.fb.accounts[0] || {}).id || '';
        config.fb_privacy = 'EVERYONE';
        break;

      case 'group':
        config.feed_id = (this.fb.groups[0] || {}).id || '';
        config.fb_privacy = 'EVERYONE';
        break;

      case 'event':
        config.feed_id = (this.fb.events[0] || {}).id || '';
        config.fb_privacy = 'EVERYONE';
        break;
    }
  }

  addNewMultiUseChannel(config) {
    const { $uibModal, RTMPPushChannel } = this;

    $uibModal
      .open({
        controllerAs: 'vm',
        template: require('../pages/settings/modal-add-multi-use-rtmp-channel.html'),
        controller: require('../pages/settings/modal-add-multi-use-rtmp-channel').default,
        controllerAs: 'vm',
        resolve: {
          mode: () => 'add',
          config: () => ({ max_resolution: 0 }),
        },
      })
      .result.then((r) => {
        this.rtmpPushChannels = RTMPPushChannel.query(() => {
          config.rtmp_push_channel_id = r.id;
        });
      });
  }

  uploadPreviewImage(file) {
    file.upload().then((result) => {
      this.broadcast.content_settings = this.broadcast.content_settings || {};
      this.broadcast.content_settings.preview = result.view_url;
      this.broadcast.content_settings.poster = result.view_url;
    });
  }

  uploadPodcastPreviewImage(file) {
    file.upload().then((result) => {
      this.broadcast.content_settings = this.broadcast.content_settings || {};
      this.broadcast.content_settings.podcast_preview = result.view_url;
    });
  }

  updateScoreboardMakes() {
    const { FeatureFlags } = this;

    // Grab the list of valid makes for the selected sport
    this.scoreboardMakes = [];

    if (this.isBoxCaster) {
      _.each(this.scoreboards, (s) => {
        if (s.require_tag && !FeatureFlags[s.require_tag]) {
          return;
        }
        if (!this.bulkImportMode) {
          if (s.value == 'BoxCast' || s.value == 'Sportzcast' || s.value == 'Manual') {
            // skip scoreboard makes that are handled separately in the UI
            return;
          }
        }
        _.each(s.models, (m) => {
          if (
            !this.broadcast.scoreboard_configuration.sport ||
            m.sports.indexOf(this.broadcast.scoreboard_configuration.sport) >= 0
          ) {
            this.scoreboardMakes.push(s);
            return false;
          }
        });
      });
    } else if (this.bulkImportMode) {
      // Only manual and Sportzcast allowed without a BoxCaster
      if (FeatureFlags.manual_scoreboard) {
        this.scoreboardMakes.push(_.find(this.scoreboards, { value: 'Manual' }));
      }
      if (FeatureFlags.sportzcast) {
        this.scoreboardMakes.push(_.find(this.scoreboards, { value: 'Sportzcast' }));
      }
    } else {
      // Only manual and Sportzcast allowed without a BoxCaster, and these are
      // handled directly in the UI
    }

    // See if the make the user has selected is valid per this sport, otherwise reset it
    if (
      !this.scoreboardMakes ||
      !_.find(this.scoreboardMakes, {
        value: this.broadcast.scoreboard_configuration.scoreboard_make,
      })
    ) {
      this.broadcast.scoreboard_configuration.scoreboard_make = null;
    }

    this.updateScoreboardModels();
  }

  updateScoreboardModels() {
    // Grab the list of valid models for the selected make
    var selectedMake = _.find(this.scoreboards, {
      value: this.broadcast.scoreboard_configuration.scoreboard_make,
    });
    var allModels = _.result(selectedMake, 'models');
    this.scoreboardModels = [];
    _.each(allModels, (m) => {
      if (
        !this.broadcast.scoreboard_configuration.sport ||
        m.sports.indexOf(this.broadcast.scoreboard_configuration.sport) >= 0
      ) {
        this.scoreboardModels.push(m);
      }
    });
    //console.log('New scoreboard models', this.scoreboardModels);

    // See if the model the user has selected is valid per this make, otherwise reset it
    if (
      !this.scoreboardModels ||
      !_.find(this.scoreboardModels, {
        value: this.broadcast.scoreboard_configuration.scoreboard_model,
      })
    ) {
      this.broadcast.scoreboard_configuration.scoreboard_model = null;
    }
    // If the selected make only has 1 model, go ahead and pre-select it
    if (this.scoreboardModels && this.scoreboardModels.length == 1) {
      this.broadcast.scoreboard_configuration.scoreboard_model = this.scoreboardModels[0].value;
    }
  }

  getConflictingEvents(refreshDayEvents = false) {
    const getEventsOnSelectedDayFn = () => {
      // NOTE: the dates used in the query need to be converted to UTC directly (not just with ISO 8601 offset specified)
      let dateStart = moment(this.startDate).startOf('day').utc().format();
      let dateEnd = moment(this.startDate).startOf('day').add(1, 'days').utc().format();
      let query = `+starts_at:[${dateStart} TO ${dateEnd}]`;
      return this.Broadcast.query({ q: query, l: 1000 }, (events) => {
        // Get rid of this event
        this.otherEvents = _.reject(events, { id: this.broadcast.id });
      }).$promise;
    };

    const getConflictingEventsFn = () => {
      // First, find other events on the same source
      let eventsFromThisSource = [];
      if (
        this.broadcast.boxcaster_id &&
        this.broadcast.boxcaster_id.startsWith('stream_source:rtmp')
      ) {
        // RTMP
        if (this.broadcast.boxcaster_id.startsWith('stream_source:rtmp:')) {
          // static RTMP
          const staticRtmpId = this.broadcast.boxcaster_id.replace('stream_source:rtmp:', '');
          eventsFromThisSource = _.filter(
            this.otherEvents,
            (b) => b.stream_source === 'rtmp' && b.static_rtmp_id === staticRtmpId
          );
        } else {
          // single-use RTMP
          eventsFromThisSource = _.filter(
            this.otherEvents,
            (b) => b.stream_source === 'rtmp' && b.static_rtmp_id === ''
          );
        }
      } else if (
        this.broadcast.boxcaster_id &&
        this.broadcast.boxcaster_id.startsWith('stream_source:app')
      ) {
        // Broadcaster
        eventsFromThisSource = _.filter(this.otherEvents, (b) => b.stream_source === 'app');
      } else if (
        this.broadcast.boxcaster_id &&
        !this.broadcast.boxcaster_id.startsWith('stream_source:')
      ) {
        // BoxCaster
        eventsFromThisSource = _.filter(
          this.otherEvents,
          (b) => b.stream_source === 'boxcaster' && b.boxcaster_id === this.broadcast.boxcaster_id
        );
      } else if (this.broadcast.boxcaster_id == 'stream_source:recording') {
        // Simulated Live (ignoring for now)
        // TODO: FIXME: attempt a better overlap check, but may require a little
        // more work to understand durations
        this.worstCaseSimultaneousStreams = 0;
        return;
      }

      // Next, find any that overlap
      const dates = adjustBroadcastDatesForCrossingMidnight(
        this.startDate,
        this.startTime,
        this.endTime
      );
      const startsAt = Date.parse(dates.starts_at);
      const stopsAt = Date.parse(dates.stops_at);
      this.conflictingEvents = _.filter(eventsFromThisSource, (b) => {
        const thisStartsAt = Date.parse(b.starts_at);
        const thisStopsAt = Date.parse(b.stops_at);
        return !(stopsAt <= thisStartsAt || thisStopsAt <= startsAt);
      });

      // Next, see how many other events are running at this time NOT from this source (to see how
      // many simultaneous) broadcasts might be going at once
      const eventsNotFromThisSource = _.reject(this.otherEvents, (b) =>
        _.some(eventsFromThisSource, (e) => e.id === b.id)
      );
      const conflictingEventsNotFromThisSource = _.filter(eventsNotFromThisSource, (b) => {
        if (b.stream_source == 'recording') {
          // TODO: FIXME: Ignoring Simulated Live for now
          return false;
        }
        const thisStartsAt = Date.parse(b.starts_at);
        const thisStopsAt = Date.parse(b.stops_at);
        return !(stopsAt <= thisStartsAt || thisStopsAt <= startsAt);
      });

      // Finally, see how many unique "sources" there are for these conflicting events from other
      // sources. While the static RTMP and BoxCaster sources can only stream one broadcast at a
      // time, single-use RTMP and broadcaster broadcasts can (in theory) be used simultaneously.
      // Therefore, the length of this source list is the worst-case number of simultaneous
      // broadcasts at the proposed time. It could actually be less than this. But we use this
      // number to indicate to the user that they may hit their stream limit.
      const otherConflictingEventsSources = _.uniq(
        _.map(conflictingEventsNotFromThisSource, (b) => {
          if (b.stream_source === 'rtmp') {
            if (b.static_rtmp_id === '') {
              return `single-use-rtmp-${b.id}`;
            } else {
              return b.static_rtmp_id;
            }
          } else if (b.stream_source === 'app') {
            return `broadcaster-${b.id}`;
          } else {
            return b.boxcaster_id;
          }
        })
      );
      this.worstCaseSimultaneousStreams = otherConflictingEventsSources.length;
    };

    if (refreshDayEvents) {
      getEventsOnSelectedDayFn().then(getConflictingEventsFn);
    } else {
      getConflictingEventsFn();
    }
  }

  compensateEndDate() {
    // Make sure end date is same day-of-month as start date
    // NOTE: Need to be careful to make minimum set of mutations in here since these objects
    // are in an angular watcher and will choke the digest cycle if changed willy nilly.
    let start = moment(this.startTime);
    let end = this.endTime ? moment(this.endTime) : null;

    if (!end) {
      // Open-ended event
    } else if (
      this.originalStartTime &&
      this.originalEndTime &&
      this.originalStartTime.getTime() != this.startTime.getTime() &&
      this.originalEndTime.getTime() == this.endTime.getTime()
    ) {
      // User changed start time but not end ... keep end locked 30 mins after start
      this.endTime = start.clone().add(30, 'minutes').toDate();
      this.originalEndTime = new Date(this.endTime.getTime());
      this.originalStartTime = new Date(this.startTime.getTime()); //avoid infinite digest
      //console.log('keeping end time locked 30 mins after start');
    } else if (end.format('HH:mm') < start.format('HH:mm')) {
      // Compensate for rolling across midnight (keep hours/minutes but bump to day after start)
      if (end.subtract(1, 'days').format('YYYY-MM-DD') != start.format('YYYY-MM-DD')) {
        this.endTime = end
          .set({
            year: start.get('year'),
            month: start.get('month'),
            date: start.get('date'),
          })
          .add(1, 'days')
          .toDate();
        //console.log('adding a day to end datetime (rolled across midnight)');
      } else {
        //console.log('already compensated?');
      }
    } else if (end.format('YYYY-MM-DD') != start.format('YYYY-MM-DD')) {
      this.endTime = end
        .set({
          year: start.get('year'),
          month: start.get('month'),
          date: start.get('date'),
        })
        .toDate();
      //console.log('resetting end date');
    } else {
      //console.log('nothing to see here');
    }
  }

  explodeStreamSourceSettings(broadcast) {
    if (this.isBoxCaster) {
      broadcast.stream_source = 'boxcaster';
    } else {
      broadcast.stream_source = broadcast.boxcaster_id.replace('stream_source:', '');
      broadcast.boxcaster_id = null;
    }
    // Deal with static RTMP channel
    if (broadcast.stream_source.startsWith('rtmp:')) {
      broadcast.static_rtmp_id = broadcast.stream_source.replace('rtmp:', '');
      broadcast.stream_source = 'rtmp';
    }
  }

  validateAll() {
    this.errors = [];
    var result = true;
    result = this.validateOverview() && result;
    result = this.validateSchedule() && result;
    result = this.validateScoreboard() && result;
    result = this.validatePublish() && result;
    result = this.validateAdvanced() && result;
    if (result && this.$scope.form.$invalid) {
      console.dir(this.$scope.form);
      var e = this.$scope.form.$error || {};
      if (e.required) {
        this.errors.push('Please fill out all required fields');
      } else {
        this.errors.push('Please check all fields for proper values');
      }
      result = false;
    }
    return result;
  }

  validateOverview() {
    var result = true;
    if (!this.broadcast.name) {
      this.errors.push('Broadcast Name is required');
      result = false;
    }
    if (!this.broadcast.boxcaster_id) {
      this.errors.push('Source is required');
      result = false;
    }
    if (this.isSimulatedLive && !this.broadcast.source_broadcast_id) {
      this.errors.push('Source broadcast is required');
      result = false;
    }
    return result;
  }

  get isInThePast() {
    // if this event is set to happen entirely in the past, don't allow it to be scheduled
    const now = new Date().getTime();
    const dates = adjustBroadcastDatesForCrossingMidnight(
      this.startDate,
      this.startTime,
      this.endTime,
      this.maxBroadcastDurationHours
    );
    const startsAt = moment(dates.starts_at).toDate().getTime();
    const stopsAt = moment(dates.stops_at).toDate().getTime();
    return now > startsAt && now > stopsAt;
  }

  validateSchedule() {
    var result = true;
    if (this.broadcast.timeframe != 'past') {
      if (!this.isWithinAccountBroadcastDurationLimit) {
        this.errors.push(
          `You are not allowed to stream for longer than ${this.maxBroadcastDurationHours} hours (contact us if you would like to upgrade your account)`
        );
        result = false;
      }
      if (this.recordingStorageExpires) {
        this.errors.push(
          'The recording storage expires before the simulated live broadcast is scheduled to begin.'
        );
        result = false;
      }
      if (this.isInThePast) {
        this.errors.push('A broadcast may not be scheduled in the past.');
        result = false;
      }
    }
    return result;
  }

  validateScoreboard() {
    var result = true;
    var sc = this.broadcast.scoreboard_configuration;
    if (this.broadcast.timeframe == 'past' || !sc.sport) {
      return result;
    }

    if (sc.manual) {
      // manual scoreboard settings do not need validated
    } else if (sc.connection == 'scoreboard_adapter') {
      if (!sc.scoreboard_make || !sc.scoreboard_model) {
        this.errors.push('Scoreboard Make and Model are required');
        result = false;
      }
    } else if (sc.connection == 'scoreboard_assist') {
      // scoreboard assist settings do not need validated
    } else if (sc.connection == 'scorebot') {
      if (isNaN(parseInt(sc.sportzcast_bot_number, 10))) {
        this.errors.push('A valid ScoreBOT number is required');
        result = false;
      }
    } else {
      this.errors.push('A scoreboard connection is required');
      result = false;
    }

    return result;
  }

  validatePublish() {
    var result = true;
    if (!this.visibility) {
      this.errors.push(`Broadcast Type is required`);
      result = false;
    }
    if (this.visibility == 'ticketed' || this.visibility == 'flex') {
      if (!this.broadcast.ticket_price || this.broadcast.ticket_price < this.minTicketPrice) {
        this.errors.push(`The ticket price must be at least ${this.minTicketPrice}`);
        result = false;
      }
    }
    let validSimulcast = this.broadcast.simulcast_configurations
      .filter((s) => s.destination === 'generic')
      .every((s) => IsRtmpURLValid(s.stream_url));
    if (!validSimulcast) {
      this.errors.push('Invalid RTMP URL');
      result = false;
    }
    let validFbSimulcast = this.broadcast.simulcast_configurations
      .filter((s) => s.destination === 'facebook')
      .every((s) => s.feed_type === 'user:me' || !!s.feed_id);
    if (!validFbSimulcast) {
      this.errors.push('Invalid Facebook Destination');
      result = false;
    }
    let validMaxResolution = this.broadcast.simulcast_configurations
      .filter((s) => s.destination === 'generic')
      .every((s) => !!s.max_resolution);
    if (!validMaxResolution) {
      this.errors.push('Invalid Max Resolution');
      result = false;
    }
    if (
      this.simulcastOnly &&
      this.simulcastLimit === 1 &&
      this.broadcast.simulcast_configurations.length !== 1
    ) {
      this.errors.push('Please select a single destination');
      result = false;
    }
    return result;
  }

  validateAdvanced() {
    var result = true;

    if (this.broadcast.simulcast_configurations.length > 0 && this.isAudioOnly) {
      this.errors.push('Audio-only broadcasts cannot be sent to simulcast destinations.');
      result = false;
    }

    var hostRestrictions = this.broadcast.content_settings.hostRestrictions;
    if (hostRestrictions) {
      if (!hostRestrictions.allowedHosts.length) {
        this.errors.push('At least one allowed host is required');
        result = false;
      }
      hostRestrictions.allowedHosts.forEach((h) => {
        if (!h) {
          this.errors.push('Allowed host cannot be blank');
          result = false;
        }
      });
    } else {
      delete this.broadcast.content_settings.hostRestrictions;
    }

    var passwordBlock = this.broadcast.content_settings.cryptblock;
    if (passwordBlock) {
      if (!passwordBlock.__password__ && !passwordBlock.crypt) {
        this.errors.push('Please choose a password');
        result = false;
      }
    } else {
      delete this.broadcast.content_settings.cryptblock;
    }

    return result;
  }

  get isWithinAccountBroadcastDurationLimit() {
    if (!this.endTime) return true;
    if (this.isSimulatedLive) return true; // ¯\_(ツ)_/¯
    var dates = adjustBroadcastDatesForCrossingMidnight(
      this.startDate,
      this.startTime,
      this.endTime,
      this.maxBroadcastDurationHours
    );
    var duration = (moment(dates.stops_at) - moment(dates.starts_at)) / (60 * 60 * 1000);
    return duration <= this.maxBroadcastDurationHours;
  }

  get recordingStorageExpires() {
    if (this.broadcast.timeframe && this.broadcast.timeframe !== 'past') {
      return false;
    }
    if (!this.isSimulatedLive) {
      return false;
    }
    if (!this.sourceBroadcast) {
      return false;
    }
    if (this.account.storage_policy !== 'auto-delete') {
      return false;
    }
    const storageExpiresAt = Date.parse(this.sourceBroadcast.storage_expires_at);
    const dates = adjustBroadcastDatesForCrossingMidnight(
      this.startDate,
      this.startTime,
      this.endTime
    );
    const startsAt = Date.parse(dates.starts_at);
    return storageExpiresAt < startsAt;
  }

  get showScrambleOption() {
    return this.visibility == 'private' && this.origVisibility != 'private';
  }

  get showAddSimulcast() {
    if (
      this.broadcast &&
      this.broadcast.simulcast_configurations &&
      this.broadcast.simulcast_configurations.length >= this.simulcastLimit
    ) {
      return false;
    }
    return (
      this.visibility != 'ticketed' &&
      this.visibility != 'flex' &&
      ((this.allowFacebookLive && !this.fb.loading && this.fb.connected) ||
        (this.allowYoutubeLive && this.youtubeUsers.length) ||
        (this.allowTwitter && this.twitterUsers.length) ||
        (this.allowGenericRtmpPush && this.rtmpPushChannels.length) ||
        this.allowGenericRtmpPush)
    );
  }

  saveChanges(skipFormValidation = false, skipCustomStreamSourceHandling = false) {
    const { $scope, $q, Broadcast, toaster } = this;

    let deferred = $q.defer();

    if (!skipFormValidation && !this.validateAll()) {
      console.log('Invalid form', this.errors);
      deferred.reject('invalid');
      return deferred.promise;
    }

    // Adjust dates when broadcast crosses midnight
    var dates = adjustBroadcastDatesForCrossingMidnight(
      this.startDate,
      this.startTime,
      this.endTime,
      this.maxBroadcastDurationHours
    );
    this.broadcast.starts_at = dates.starts_at;
    this.broadcast.stops_at = dates.stops_at;

    // Make a copy so we can mutate the original in ways that might be incompatible with the
    // edit/create pages
    let broadcast = angular.copy(this.broadcast);

    // Clean up the visibility settings on the broadcast object
    broadcast = updatePriceSettingsFromVisibilityOption(broadcast, this.visibility);

    // Clean up cruft from UI shenanigans
    checkContentSettingsForChangesOnSave(broadcast.content_settings);
    if (broadcast.content_settings.preview === null) {
      delete broadcast.content_settings.preview;
    }

    if (!this.bulkImportMode) {
      mapScoreboardUIConfigurationToAPI(broadcast.scoreboard_configuration);
    }

    if (this.showFeatured) {
      if (this.isFeatured) {
        if (!_.contains(broadcast.tags, 'featured')) {
          broadcast.tags.push('featured');
        }
      } else {
        _.remove(broadcast.tags, (t) => t == 'featured');
      }
    }

    // Simulcast configuration managed separately
    let simulcastMeta = broadcast.simulcast_configurations;
    delete broadcast.simulcast_configurations;

    // Remove instructional rows
    simulcastMeta.filter((s) => !s.isHelpRow);

    // Someone may have clicked the "Multi-Use" tab to see what that was about _AFTER_ filling out
    // the single-use fields. In that case, flip it back to generic.
    simulcastMeta
      .filter((s) => s.destination === 'rtmp_push_channel')
      .forEach((s) => {
        if (!s.rtmp_push_channel_id && s.stream_url && s.stream_key) {
          s.destination = 'generic';
          if (!s.max_resolution) {
            s.max_resolution = '0';
          }
        }
      });

    // string to integers for max_resolution
    simulcastMeta
      .filter((s) => s.destination === 'generic')
      .forEach((s) => (s.max_resolution = parseInt(s.max_resolution)));

    // filter out any multi-use RTMP that don't actually have a channel selected
    simulcastMeta = simulcastMeta.filter(
      (s) => s.destination !== 'rtmp_push_channel' || !!s.rtmp_push_channel_id
    );

    // Recombine feed type and ID for facebook
    simulcastMeta
      .filter((s) => s.destination === 'facebook')
      .forEach((s) => {
        if (s.feed_type === 'user:me') {
          s.fb_feed_id = 'user:me';
        } else {
          s.fb_feed_id = s.feed_type + ':' + s.feed_id;
        }
        delete s.feed_type;
        delete s.feed_id;
      });

    // Support regenerating the channel ID
    if (this.showScrambleOption && this.scrambleChannelId) {
      broadcast.regenerate_channel_id = '1';
      broadcast.metadata = broadcast.metadata || {};
      broadcast.metadata.original_channel_id =
        broadcast.metadata.original_channel_id || broadcast.channel_id;
    } else {
      delete broadcast.regenerate_channel_id;
    }

    // Deal with custom stream sources
    if (!skipCustomStreamSourceHandling) {
      this.explodeStreamSourceSettings(broadcast);
    }

    // Handle start/stop time editibility
    if (broadcast.stream_source != 'imported') {
      if (broadcast.timeframe == 'current') {
        // If this broadcast is live, API won't let us change the start time
        delete broadcast.starts_at;
      } else if (broadcast.timeframe == 'past') {
        // If this broadcast was in the past, API won't let us change the start or stop time
        delete broadcast.starts_at;
        delete broadcast.stops_at;
      }
    }

    // Remove unnecessary fields for imported/simulated live broadcasts
    if (broadcast.stream_source == 'imported' || broadcast.stream_source == 'recording') {
      broadcast.target_video_bitrate = null;
      broadcast.target_audio_bitrate = null;
      broadcast.audio_source = null;
      broadcast.target_video_resolution = null;
      broadcast.target_high_frame_rate = null;
      broadcast.dscp_value = null;
    }

    // Simulated live broadcasts have some special values
    if (broadcast.stream_source == 'recording') {
      broadcast.requests_captioning = false;
      if (broadcast.stops_at) {
        broadcast.stops_at = null;
      }
    }

    // Do not attempt to change the stream source for *past* broadcasts (can cause
    // weird issues with archived BoxCasters, RTMP Sources, etc.)
    if (broadcast.timeframe == 'past') {
      delete broadcast.stream_source;
      delete broadcast.static_rtmp_id;
      delete broadcast.boxcaster_id;
      delete broadcast.source_broadcast_id;
    }

    if (broadcast.timeframe && broadcast.timeframe != 'future') {
      // Do not attempt to change the target_buffer_latency for any non-future broadcast
      delete broadcast.target_buffer_latency;
    } else {
      // Make sure target_buffer_latency makes sense
      if (this.account.feature_flags.boxcast_flow_control_disabled) {
        broadcast.target_buffer_latency = 'default';
      } else if (this.isBoxCasterHDSD2) {
        if (broadcast.target_buffer_latency == 'maximum') {
          broadcast.target_buffer_latency = 'enhanced';
        }
      } else if (!this.isBoxCasterHDSD2 && !this.isBoxCasterPro && !this.isBroadcasterApp) {
        broadcast.target_buffer_latency = 'default';
      }
    }

    // Detach from old static RTMP channel if switching to non-RTMP source
    if (broadcast.stream_source != 'rtmp' && broadcast.static_rtmp_id) {
      broadcast.static_rtmp_id = null;
    }

    let promise;
    if (broadcast.id) {
      // Update existing broadcast
      if (broadcast.timeframe != 'past') {
        broadcast.simulcast_configurations = simulcastMeta;
      } else {
        delete broadcast.scoreboard_configuration;
      }
      promise = Broadcast.update({ id: broadcast.id }, broadcast).$promise;
    } else {
      // Save new broadcast
      broadcast.simulcast_configurations = simulcastMeta;
      promise = Broadcast.save(broadcast).$promise;
    }

    promise
      .then((response) => {
        // Handle any warnings/errors from simulcast
        (response.simulcast_configurations || []).forEach((c) => {
          if (c.error) {
            console.error(c);
            toaster.pop(
              'warning',
              `There was an error saving the simulcast configuration: ${c.error.error_description}`
            );
          }
        });
        deferred.resolve(response);
      })
      .catch((error) => {
        deferred.reject(error);
      });
    return deferred.promise;
  }
}

module.service('EditOrCreateEventService', EditOrCreateEventService);

export default module;



// WEBPACK FOOTER //
// ./app/services/edit-or-create-event-service.js